import { useState, useEffect, useCallback } from 'react';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { getSessionId } from '../utils/sessionId';
import { HTTP_STATUS } from '../utils/httpStatusCodes';
import { toast } from 'react-toastify';

import {
isOfflineAndroid,
getOfflineCart,
addOfflineCartItem,
updateOfflineCartItem,
removeOfflineCartItem,
getOpenTableOrders
} from '../utils/androidOfflineCart';


const useCart = (refresh, editOrderId = null, { skipInitialFetch = false, mergePending = false,  tableId = null } = {}) => {
  const [cartItems, setCartItems] = useState([]);
  const [netTotal, setNetTotal] = useState(0);
  const [dealsItem, setDealsItem] = useState([]);
  const [halfAndHalfItems, setHalfAndHalfItems] = useState([]);
  const [removeID, setRemoveID] = useState(null);
  const [servers, setServers] = useState([]);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState('');


const mergeOfflineTableOrders = useCallback(async () => {
  if (!tableId) {
    console.log('[OFFLINE_TABLE] No tableId');
    return [];
  }

  try {
    console.log('[OFFLINE_TABLE] Calling getOpenTableOrders for tableId:', tableId);

    const response = await getOpenTableOrders(tableId);

    if (!response || response.success !== true) {
      console.warn('[OFFLINE_TABLE] Invalid response:', response);
      return [];
    }

    const previousItems = [];
    const orders = Array.isArray(response?.orders) ? response.orders : [];

    orders.forEach((order, orderIndex) => {
      const items = Array.isArray(order?.items) ? order.items : [];

      items.forEach((item, itemIndex) => {
        const unitPrice = Number(item?.unitPrice ?? item?.per_item_price ?? item?.price ?? 0);
        const qty = Number(item?.quantity ?? item?.item_quantity ?? 1);
        const totalPrice = Number(item?.totalPrice ?? item?.total_price ?? (unitPrice * qty));
        
        // Ensure robust unique ID fallback for rendering keys
        const uniqueCartId = item?.cart_id || item?.localItemId || `prev_${order?.localOrderId || orderIndex}_${itemIndex}`;
        const productIdStr = String(item?.productId || item?.item_id || item?.id || '');

        const mappedItem = {
          ...item,
          cart_id: uniqueCartId,
          localItemId: uniqueCartId,
          localOrderId: item?.localOrderId || order?.localOrderId || '',
          item_id: productIdStr,
          productId: productIdStr,
          item_name: item?.item_name || item?.name || item?.itemname || 'Unknown Item',
          name: item?.name || item?.item_name || item?.itemname || 'Unknown Item',
          item_quantity: qty,
          quantity: qty,
          per_item_price: unitPrice,
          unitPrice: unitPrice,
          total_price: totalPrice,
          totalPrice: totalPrice,
          selected_size: item?.selected_size || item?.sizeName || '',
          sizeName: item?.sizeName || item?.selected_size || '',
          sizeId: item?.sizeId || null,
          is_previously_added: true,
          isPreviouslyAdded: true
        };

        if (!order?.isCart) {
          console.log(`[OFFLINE_TABLE] PREVIOUS ITEM [${orderIndex}/${itemIndex}]:`, mappedItem);
          previousItems.push(mappedItem);
        }
      });
    });

    console.log('[OFFLINE_TABLE] FINAL PREVIOUS ITEMS:', previousItems);
    return previousItems;

  } catch (error) {
    console.error('[OFFLINE_TABLE] Failed getting table orders:', error);
    return [];
  }
}, [tableId]);



const loadOfflineCart = useCallback(async () => {
  console.log(`[OFFLINE_CART] LOAD CART START - tableId: ${tableId}`);

  /*
   * 1. CURRENT SESSION CART (Wrapped in try/catch so failure doesn't block open orders)
   */
  let currentSessionItems = [];
  try {
    const response = await getOfflineCart();
    const rawSessionItems = Array.isArray(response?.items) ? response.items : [];

    currentSessionItems = rawSessionItems.map((item, index) => {
      const unitPrice = Number(item?.per_item_price ?? item?.unitPrice ?? item?.price ?? 0);
      const qty = Number(item?.item_quantity ?? item?.quantity ?? 1);
      const totalPrice = Number(item?.total_price ?? item?.totalPrice ?? (unitPrice * qty));
      const uniqueCartId = item?.cart_id || item?.localItemId || `session_${index}`;
      const productIdStr = String(item?.productId || item?.item_id || item?.id || '');

      return {
        ...item,
        cart_id: uniqueCartId,
        localItemId: uniqueCartId,
        item_id: productIdStr,
        productId: productIdStr,
        item_name: item?.item_name || item?.name || item?.itemname || 'Unknown Item',
        name: item?.name || item?.item_name || item?.itemname || 'Unknown Item',
        item_quantity: qty,
        quantity: qty,
        per_item_price: unitPrice,
        unitPrice: unitPrice,
        total_price: totalPrice,
        totalPrice: totalPrice,
        selected_size: item?.selected_size || item?.sizeName || '',
        sizeName: item?.sizeName || item?.selected_size || '',
        sizeId: item?.sizeId || null,
        is_previously_added: false,
        isPreviouslyAdded: false
      };
    });
  } catch (err) {
    console.error('[OFFLINE_CART] Failed fetching current session cart:', err);
  }

  /*
   * 2. PREVIOUS ORDERS FOR TABLE
   */
  let previousTableItems = [];
  if (tableId) {
    previousTableItems = await mergeOfflineTableOrders();
  }

  /*
   * 3. COMBINE CURRENT + PREVIOUS ITEMS (Works even when currentSessionItems is empty [])
   */
  const finalCartItems = [...previousTableItems, ...currentSessionItems];

  const combinedNetTotal = finalCartItems.reduce(
    (sum, item) => sum + (Number(item.total_price) || 0),
    0
  );

  console.log('[OFFLINE_CART] FINAL COMBINED CART ITEMS:', finalCartItems);

  setCartItems(finalCartItems);
  setDealsItem([]);
  setHalfAndHalfItems([]);
  setNetTotal(combinedNetTotal);

}, [tableId, mergeOfflineTableOrders]);

// const loadOfflineCart = useCallback(async () => {
// try {
// const response = await getOfflineCart();

// if (!response) {
//   setCartItems([]);
//   setDealsItem([]);
//   setHalfAndHalfItems([]);
//   setNetTotal(0);
//   return;
// }

// let items = Array.isArray(response.items)
//   ? response.items
//   : [];

//   console.log(
//           '[OFFLINE_ORDER] loadOfflineCart tableId:',
//           tableId
//         );
// if (tableId) {

//   const mergedItems =
//     await mergeOfflineTableOrders();

//   if (mergedItems !== null) {
//     items = mergedItems;
//   }

// }

// setCartItems(items);
// setDealsItem([]);
// setHalfAndHalfItems([]);

// setNetTotal(
//   Number(response.total ?? 0)
// );

// console.log(
//   '[OFFLINE_CART] Room cart loaded:',
//   response
// );


// } catch (error) {


// console.error(
//   '[OFFLINE_CART] Failed loading Room cart:',
//   error
// );

// setCartItems([]);
// setDealsItem([]);
// setHalfAndHalfItems([]);
// setNetTotal(0);


// }
// }, [tableId, mergeOfflineTableOrders]);


 const fetchCartWithDeals = useCallback(async () => {
  if (isOfflineAndroid()) {
    console.log('[OFFLINE_CART] Loading cart from Android Room');
    await loadOfflineCart();
    return;
  }

  const session_id = getSessionId();

  let url =apiUrls.GET_ADDED_TO_CART +`?session_id=${session_id}`;

  if (editOrderId) {
    url += `&edit_order_id=${editOrderId}`;
  }

 if (mergePending) {
      url += `&merge_pending=1`;
 }

  const response = await callAPI(url,{},'GET',null,{},{},false);
  if (response?.data?.data) {
    setCartItems( response.data.data.items || [])
    setDealsItem(response.data.data.data_deals || []);
    setHalfAndHalfItems(response.data.data.data_half_and_half || []);
    setNetTotal(response.data.data.net_total || 0);
    setServers(response.data.servers || []);
    console.log('CART ITEMS', response.data.data.items);
    console.log('DEAL ITEMS',response.data.data.data_deals);
  }
}, [editOrderId,loadOfflineCart]);





  const fetchCartDetails = async () => {
    const session_id = getSessionId();
    let url = apiUrls.CART_ITEM_DETAILS + `?session_id=${session_id}`;
    if (editOrderId) {
      url += `&edit_order_id=${editOrderId}`;
    }
    const response = await callAPI(
      url,
      {},
      'GET'
    );
    if (response?.data?.data) {
      setCartItems(response.data.data.items || []);
      setNetTotal(response.data.data.net_total || 0);
    }
  };





const handleUpdateQty = async (
cartItem,
newQty
) => {

try {

if (isOfflineAndroid()) {

  if (
    !cartItem?.localOrderId ||
    !cartItem?.localItemId
  ) {
    console.error(
      '[OFFLINE_CART] Missing local cart identifiers:',
      cartItem
    );

    toast.error(
      'Unable to update offline cart item'
    );

    return;
  }

  await updateOfflineCartItem({
    localOrderId:
      cartItem.localOrderId,

    localItemId:
      cartItem.localItemId,

    quantity:
      newQty
  });


  await new Promise(
    resolve => setTimeout(resolve, 150)
  );

  await loadOfflineCart();

  toast.success(
    'Quantity updated'
  );

  return;
}



const cartId =
  typeof cartItem === 'object'
    ? cartItem.cart_id
    : cartItem;

const response = await callAPI(
  apiUrls.UPDATE_QUANTITY,
  {},
  'POST',
  {
    id: cartId,
    quantity: newQty
  }
);

if (
  response &&
  response.status === HTTP_STATUS.OK
) {

  toast.success(
    'Quantity updated'
  );

  await fetchCartWithDeals();

} else {

  console.error(
    'Error updating quantity:',
    response
  );

  toast.error(
    'Failed to update quantity'
  );
}


} catch (error) {


console.error(
  'Error updating quantity:',
  error
);

toast.error(
  'Failed to update quantity'
);


}
};



const handleRemoveItem = async (
cartItem
) => {

try {

setRemoveID(
  typeof cartItem === 'object'
    ? cartItem.localItemId || cartItem.cart_id
    : cartItem
);



if (isOfflineAndroid()) {

  if (
    !cartItem?.localOrderId ||
    !cartItem?.localItemId
  ) {

    console.error(
      '[OFFLINE_CART] Missing local cart identifiers:',
      cartItem
    );

    toast.error(
      'Unable to remove offline cart item'
    );

    setRemoveID(null);

    return false;
  }

  await removeOfflineCartItem({

    localOrderId:
      cartItem.localOrderId,

    localItemId:
      cartItem.localItemId

  });

  await new Promise(
    resolve => setTimeout(resolve, 150)
  );

  await loadOfflineCart();

  toast.success(
    'Item removed'
  );

  setRemoveID(null);

  return true;
}



const cartId =
  typeof cartItem === 'object'
    ? cartItem.cart_id
    : cartItem;

const session_id =
  getSessionId();

const payload = {
  id: cartId,
  session_id
};

if (editOrderId) {
  payload.edit_order_id =
    editOrderId;
}

const response =
  await callAPI(
    apiUrls.REMOVE_CART_ITEM,
    {},
    'POST',
    payload
  );

if (
  response &&
  response.status === HTTP_STATUS.OK
) {

  toast.success(
    'Item removed'
  );

  await fetchCartWithDeals();

  setRemoveID(null);

  return true;

} else {

  console.error(
    'Error removing item:',
    response
  );

  toast.error(
    'Failed to remove item'
  );
}


} catch (error) {


console.error(
  'Error removing item:',
  error
);

toast.error(
  'Failed to remove item'
);


}

setRemoveID(null);

return false;
};



  useEffect(() => {
    if (skipInitialFetch) return;
    const fetchData = async () => {
      await fetchCartWithDeals();
      //await fetchCartDetails();
    };
    fetchData();
  }, [refresh, editOrderId, tableId, fetchCartWithDeals, skipInitialFetch]);


const mapOfflineCartItem = (item) => ({
    cart_id: item.localItemId,
    local_item_id: item.localItemId,
    local_order_id: item.localOrderId,

    item_id: item.productId,
    itemname: item.name,

    quantity: item.quantity,
    price: item.unitPrice,
    total_price: item.totalPrice,

    size_id: item.sizeId,
    size_name: item.sizeName,

    comments: item.comments,
    extra_options_json: item.extraOptionsJson,
    product_detail_json: item.productDetailJson,

    backend_cart_item_id: item.backendCartItemId,

    is_previously_added: !!item.isPreviouslyAdded
});



useEffect(() => {

  if (!window.AndroidPOS?.updateCart) return;

  const timeout = setTimeout(() => {

    const androidItems = [
      ...cartItems,
      ...dealsItem.map(deal => ({
        cart_id: deal.cart_id,
        item_id: deal.product_id || deal.item_id,
        item_name: deal.item_name,
        item_quantity: deal.item_quantity || 1,
        first_image_url: deal.product_thumbnail,
        total_price: deal.totalwithdeals ?? deal.total_price,
        per_item_price: deal.totalwithdeals ?? deal.total_price,
        selected_size: "",
        product_detail_text: ""
      }))
    ];

    window.AndroidPOS.updateCart(JSON.stringify({
      items: androidItems,
      total: netTotal
    }));

  },150);

  return () => clearTimeout(timeout);

}, [cartItems, dealsItem, netTotal]);



useEffect(() => {
  const checkBridge = setInterval(() => {
    if (window.AndroidPOS?.updateCart) {
      clearInterval(checkBridge);
    }
  }, 500);

  return () => clearInterval(checkBridge);
}, []);


const resetCart = useCallback(() => {
  setCartItems([]);
  setDealsItem([]);
  setHalfAndHalfItems([]);
  setNetTotal(0);

  if (window.AndroidPOS?.updateCart) {
    window.AndroidPOS.updateCart(JSON.stringify({
      items: [],
      total: 0
    }));
  }
}, []);


  return {
    cartItems,
    netTotal,
    dealsItem,
    halfAndHalfItems,
    removeID,
    servers,
    tooltipVisible,
    tooltipText,
    setTooltipVisible,
    setTooltipText,
    handleUpdateQty,
    handleRemoveItem,
    fetchCartWithDeals,
    fetchCartDetails,
    resetCart
  };
};

export default useCart;
